import React from "react";
import { Box } from "@mui/joy";
import DarkTypography from "components/common/DarkTypography";
import SearchInputWithDropdown from "components/common/SearchInputWithDropdown";
import IconButtonCTA from "components/common/IconButtonCTA";
import PersonChip from "components/common/PersonChip";
import type { MultiComment } from "api/zekay/comments";
import { SCreate, SClose, SUserAdd } from "styles/StyledMUI";
import { firstLastQualificationLabel } from "../../../excursions/shared/utils/excursionUtils";
import type { ExcursionGroupCardClient } from "../../../excursions/shared/utils/excursionPlanningTypes";
import type { ZekayCustomer } from "api/customers/Customer.typed";
import { colorTokens } from "styles/globalStyles";
import { normalizeCustomerEmoji } from "../../../common/customerEmoji";

const chipSx = {
  backgroundColor: colorTokens.bg.surfaceMuted,
  color: colorTokens.text.primary,
  maxWidth: "100%",
  "& .MuiChip-label": { overflow: "hidden", textOverflow: "ellipsis" },
};

export type GroupClientsSectionProps = {
  clients: ExcursionGroupCardClient[];
  groupColor: string;
  canEdit: boolean;
  clientSearchActive: boolean;
  clientSearchQuery: string;
  clientSearchResults: ZekayCustomer[];
  onClientSearchChange: (query: string) => void;
  onClientSearchFocus: () => void;
  onClientSearchClose?: () => void;
  onSelectClient: (customer: ZekayCustomer) => void;
  onCreateClientClick?: () => void;
  onRemoveClient: (clientIndex: number) => void;
  onClientColorChange?: (clientIndex: number, hex: string) => void;
  onClientCommentsChange?: (clientIndex: number, comments: MultiComment[]) => void;
  onClientDragStart?: (e: React.DragEvent, clientIndex: number) => void;
  onDragOver?: (e: React.DragEvent) => void;
  onDrop?: (e: React.DragEvent) => void;
  onOpenCustomerOverview?: (customerId: number) => void;
};

export function GroupClientsSection({
  clients,
  groupColor,
  canEdit,
  clientSearchActive,
  clientSearchQuery,
  clientSearchResults,
  onClientSearchChange,
  onClientSearchFocus,
  onClientSearchClose,
  onSelectClient,
  onCreateClientClick,
  onRemoveClient,
  onClientColorChange,
  onClientCommentsChange,
  onClientDragStart,
  onDragOver,
  onDrop,
  onOpenCustomerOverview,
}: GroupClientsSectionProps) {
  return (
    <Box onDragOver={canEdit ? onDragOver : undefined} onDrop={canEdit ? onDrop : undefined}>
      <DarkTypography level="body-xs" colorVariant="legend" sx={{ mb: 0.5 }}>
        Clients
      </DarkTypography>
      {clients.length > 0 || canEdit ? (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, alignItems: "center" }}>
          {clients.map((client, clientIndex) => (
            <PersonChip
              key={client.id}
              size="sm"
              variant="soft"
              chipColor={client.color ?? groupColor}
              onRemove={canEdit ? () => onRemoveClient(clientIndex) : undefined}
              confirmRemove={
                canEdit
                  ? { title: "Retirer le client", message: "Retirer ce client du groupe ?" }
                  : undefined
              }
              customerId={client.id}
              onOpenCustomerOverview={
                onOpenCustomerOverview != null ? () => onOpenCustomerOverview(client.id) : undefined
              }
              colorPicker={
                canEdit && onClientColorChange
                  ? {
                      value: client.color ?? groupColor,
                      onChange: (hex) => onClientColorChange(clientIndex, hex),
                      ariaLabel: "Couleur client",
                    }
                  : undefined
              }
              comments={client.comments}
              onCommentsChange={
                canEdit && onClientCommentsChange
                  ? (nextComments) => onClientCommentsChange(clientIndex, nextComments)
                  : undefined
              }
              emoji={normalizeCustomerEmoji(client.emoji ?? undefined) ?? null}
              draggable={canEdit}
              onDragStart={
                canEdit && onClientDragStart ? (e) => onClientDragStart(e, clientIndex) : undefined
              }
              lastName={client.last_name}
              firstName={client.first_name}
              qualification={client.qualification}
              inProgressTrainingsTooltip={client.inProgressTrainingsSessionsTooltip}
              sx={{
                ...chipSx,
                cursor: canEdit ? "grab" : "default",
                "&:active": canEdit ? { cursor: "grabbing" } : {},
              }}
            />
          ))}
          {canEdit &&
            (clientSearchActive ? (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  flex: { xs: "1 1 100%", sm: "1 1 120px" },
                  minWidth: 0,
                }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <SearchInputWithDropdown
                    value={clientSearchQuery}
                    onChange={(q) => onClientSearchChange(q)}
                    onFocus={onClientSearchFocus}
                    placeholder="Rechercher un client…"
                    results={clientSearchResults
                      .filter((c): c is ZekayCustomer & { Id: number } => c.Id != null)
                      .map((c) => ({
                        id: c.Id,
                        label:
                          firstLastQualificationLabel(
                            c.FirstName,
                            c.LastName,
                            c.LastQualification,
                          ) || `#${c.Id}`,
                      }))}
                    onSelect={(item) => {
                      const c = clientSearchResults.find((r) => r.Id === item.id);
                      if (c) onSelectClient(c);
                    }}
                    maxResults={10}
                    size="sm"
                  />
                </Box>
                {onCreateClientClick && (
                  <IconButtonCTA
                    size="sm"
                    variant="soft"
                    aria-label="Créer un client"
                    ctaVariant="accent"
                    onClick={onCreateClientClick}
                    sx={{ flexShrink: 0 }}
                  >
                    <SUserAdd />
                  </IconButtonCTA>
                )}
                {onClientSearchClose && (
                  <IconButtonCTA
                    size="sm"
                    variant="plain"
                    aria-label="Fermer la recherche"
                    onClick={onClientSearchClose}
                  >
                    <SClose />
                  </IconButtonCTA>
                )}
              </Box>
            ) : (
              <IconButtonCTA
                size="sm"
                variant="soft"
                aria-label="Ajouter un client"
                ctaVariant="accent"
                onClick={onClientSearchFocus}
              >
                <SCreate />
              </IconButtonCTA>
            ))}
        </Box>
      ) : (
        <DarkTypography level="body-sm" colorVariant="muted">
          —
        </DarkTypography>
      )}
    </Box>
  );
}
